import { UserStore } from './user.store';
import { Injectable, EventEmitter } from '@angular/core';
import { ApiService } from '../service/api.service';
import { PrenotazioneModel, AutocertificazioneModel } from '../interfaces/prenotazioneModel';
import DateUtil from '../utils/dateUtil';
import { SocketService } from '../service/socket.service';
import { AcuStorageService } from '../service/acustorage.service';
import { Subject } from 'rxjs';



@Injectable({
  providedIn: 'root'
})
export class PrenotazioniStore {

  public allPrenotazioni: PrenotazioneModel[] = [];

  public storeUpdated: EventEmitter<void> = new EventEmitter();
  /** Emits after a successful booking insertion — used to trigger the feedback popup */
  public prenotazioneInserita: Subject<void> = new Subject<void>();


  constructor(
    private apiService: ApiService,
    private userStore: UserStore,
    private socketService: SocketService,
    private storage: AcuStorageService
  ) {
    this.socketService.receiveUpdatePrenotazioni().subscribe((update) => {
      this.initStore();
    });
    this.socketService.updateStoreReconnected.subscribe(() => {
      console.log("updateStoreReconnected");
      if (this.userStore.userLogged) {
        this.initStore();
      }
    });

    this.userStore.storeUpdated.subscribe(() => {
      if (this.userStore.userLogged) {
        this.initStore();
      }
    });
  }

  private initStorePromise: Promise<PrenotazioneModel[]> | null = null;

  public initStore(): Promise<PrenotazioneModel[]> {
    if (this.initStorePromise) {
      return this.initStorePromise;
    }
    console.log('PrenotazioniStore - initStore');
    this.initStorePromise = (async () => {
      try {
        const res = await this.apiService.getAllPrenotazioni().toPromise();
        if (res) {
          this.allPrenotazioni = res;
          this.storage.setObjectInStorage('allPrenotazioni', res);
        } else {
          this.allPrenotazioni = [];
        }
        this.storeUpdated.emit();
        return this.allPrenotazioni;
      } catch (err) {
        console.warn('PrenotazioniStore - error fetching, attempting offline fallback', err);
        const cached = this.storage.getObjectInStorage<PrenotazioneModel[]>('allPrenotazioni');
        this.allPrenotazioni = cached || [];
        this.storeUpdated.emit();
        return this.allPrenotazioni;
      } finally {
        this.initStorePromise = null;
      }
    })();
    return this.initStorePromise;
  }

  async getAllPrenotazioni() {
    return this.apiService.getAllPrenotazioni().toPromise();
  }

  async insertPrenotazione(prenotazione: PrenotazioneModel) {
    await this.apiService.insertPrenotazione(prenotazione);
    this.prenotazioneInserita.next();
    this.initStore();
  }


  async rimuoviPrenotazione(prenotazione: PrenotazioneModel, cascade?: boolean) {
    await this.apiService.rimuoviPrenotazione(prenotazione, cascade);
    this.initStore();
  }

  async aggiornaPrenotazione(prenotazione: PrenotazioneModel) {
    await this.apiService.updatePrenotazione(prenotazione);
    this.initStore();
  }

  async salvaAutocertificaizone(autocertificazione: AutocertificazioneModel) {
    await this.apiService.salvaAutocertificazione(autocertificazione);
  }

  async getAllAutocertificazioniByUser(idUser: string): Promise<AutocertificazioneModel[]> {
    return this.apiService.getAutocertificazioniByIdUtente(idUser);
  }

  async removeAutocertificazione(id: string): Promise<any> {
    return this.apiService.deleteAutocertificazione(id);
  }

  getAllPrenotazioniByUser(idUser: string): PrenotazioneModel[] {
    return this.allPrenotazioni.filter(p => p.idUser == idUser);
  }

  getPrenotazioniByUser(idUser: string, limit: number): PrenotazioneModel[] {
    if (limit)
      return this.allPrenotazioni.filter(p => p.idUser == idUser).slice(0, limit);
    else
      return this.allPrenotazioni.filter(p => p.idUser == idUser);
  }

  getPrenotazioniByUserDateRange(idUser: string, dateStart: Date, dateEnd: Date): PrenotazioneModel[] {
    return this.allPrenotazioni.filter(p => p.idUser == idUser && DateUtil.isInDateRange(p.data, dateStart, dateEnd, undefined, "[]"));
  }

  getPrenotazioniBySedeIdUfficioAndDateRange(codiceSede: string, idUfficio: string, dateStart: Date, dateEnd: Date): PrenotazioneModel[] {
    return this.allPrenotazioni.filter(p => p.idUfficio == idUfficio && codiceSede == p.codiceSede && DateUtil.isInDateRange(p.data, dateStart, dateEnd, undefined, "[]"));
  }

  getPrenotazioniByUserDateSede(idUser: string, data: Date, codiceSede: string): PrenotazioneModel[] {
    return this.allPrenotazioni.filter(p => (p.idUser == idUser && DateUtil.compareDate(p.data, data) && codiceSede == p.codiceSede));
  }

  getPrenotazioniByUserDate(idUser: string, data: Date): PrenotazioneModel[] {
    return this.allPrenotazioni.filter(p => (p.idUser == idUser && DateUtil.compareDate(p.data, data)));
  }

  getPrenotazioniByDateSede(data: Date, codiceSede: string): PrenotazioneModel[] {
    return this.allPrenotazioni.filter(p => (DateUtil.compareDate(p.data, data) && codiceSede == p.codiceSede));
  }

  getPrenotazioniByDate(data: Date): PrenotazioneModel[] {
    return this.allPrenotazioni.filter(p => DateUtil.compareDate(p.data, data));
  }
}
